/** A failed call to the ZenFresh API, in the backend's error envelope (see backend/app/errors.py). */
export class ApiError extends Error {
  readonly status: number;
  /** Stable, machine-readable (e.g. "OTP_INVALID") — switch on this, never on `message`. */
  readonly code: string;
  readonly requestId?: string;
  /** From the Retry-After header on a 429. */
  readonly retryAfterSeconds?: number;

  constructor(status: number, code: string, message: string, requestId?: string, retryAfterSeconds?: number) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
    this.requestId = requestId;
    this.retryAfterSeconds = retryAfterSeconds;
  }
}

const NETWORK_MESSAGE = "We can't reach ZenFresh right now. Check your connection and try again.";

interface ErrorEnvelope {
  error?: { code?: unknown; message?: unknown; requestId?: unknown };
}

function asString(value: unknown): string | undefined {
  return typeof value === "string" ? value : undefined;
}

/**
 * POST JSON to `/api/...` (Firebase Hosting rewrites it to the FastAPI backend in production;
 * Vite proxies it in dev). Resolves with the parsed body or throws ApiError.
 */
export const apiPost = <T>(path: string, body: unknown, signal?: AbortSignal): Promise<T> =>
  request<T>("POST", path, body, signal);

/** GET JSON from a public `/api/...` endpoint (no sign-in). */
export const apiGet = <T>(path: string, signal?: AbortSignal): Promise<T> => request<T>("GET", path, undefined, signal);

/** Like apiPost/GET, but signed in: sends the current Firebase ID token as a Bearer token. */
export async function apiAuthed<T>(method: "GET" | "POST" | "PUT" | "DELETE", path: string, body?: unknown): Promise<T> {
  const { getFirebaseAuth } = await import("./firebaseAuth");
  const user = (await getFirebaseAuth()).currentUser;
  if (!user) throw new ApiError(401, "UNAUTHENTICATED", "Please sign in again.");
  return request<T>(method, path, body, undefined, await user.getIdToken());
}

async function request<T>(
  method: string,
  path: string,
  body: unknown,
  signal?: AbortSignal,
  token?: string,
): Promise<T> {
  let response: Response;
  try {
    response = await fetch(`/api${path}`, {
      method,
      headers: {
        ...(body === undefined ? {} : { "Content-Type": "application/json" }),
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: body === undefined ? undefined : JSON.stringify(body),
      signal,
    });
  } catch (error) {
    if (error instanceof DOMException && error.name === "AbortError") throw error;
    throw new ApiError(0, "NETWORK_ERROR", NETWORK_MESSAGE);
  }

  let data: unknown = null;
  try {
    data = await response.json();
  } catch {
    // Not JSON. Most likely the SPA's index.html: the API isn't deployed behind this origin.
  }

  if (!response.ok) {
    const envelope = (data ?? {}) as ErrorEnvelope;
    throw new ApiError(
      response.status,
      asString(envelope.error?.code) ?? "HTTP_ERROR",
      asString(envelope.error?.message) ?? "Something went wrong. Please try again.",
      asString(envelope.error?.requestId),
      Number(response.headers.get("Retry-After")) || undefined,
    );
  }
  if (data === null) {
    throw new ApiError(response.status, "BAD_RESPONSE", "The server sent an unexpected reply.");
  }
  return data as T;
}
